import React, { useEffect, useMemo, useState } from 'react';
import {
  useParams,
  useLazyQuery,
  useMutation,
  useQuery,
  useNavigate,
} from '@common/hooks';
import { Form } from 'antd';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { SUPPLIER_ADMIN_MENU } from 'works/legacy/pages/supplier/admin-settings/supplier-admin-vertical-nav-menu';
import {
  Banner,
  ErrorHandler,
  LoadingWrapper,
  PageSteps,
} from '@common/components';
import { MembersAddEditStepForm } from './members-add-edit-step-form';
import {
  MembershipByEmail,
  MembershipOrganizationUnit,
  WorksMembershipByEmailQuery,
  WorksMembershipByEmailResponse,
} from 'works/legacy/graphql/works-membership-by-email';
import {
  CustomOrganizationUnits,
  MembersAddEditStepSelectUnits,
} from './members-add-edit-step-select-units';
import { useCurrentEntity } from '@identity/hooks';
import { Store } from 'antd/lib/form/interface';
import { formatPhone } from 'works/legacy/services/phone-service';
import {
  WorksMembershipCreation,
  WorksMembershipCreationResponse,
} from 'works/legacy/graphql/works-membership-creation';
import {
  WorksMembershipUpdate,
  WorksMembershipUpdateResponse,
} from 'works/legacy/graphql/works-membership-update';
import { createMessage } from 'works/legacy/lib/message-helper';
import { MembersAddEditStepReview } from './members-add-edit-step-review';
import {
  WorksMembershipRolesQuery,
  WorksMembershipRolesResponse,
} from 'works/legacy/graphql/works-membership-roles';
import {
  Option,
  TimeZonesQuery,
  TimeZonesResponse,
} from 'works/legacy/graphql/time-zones';
import {
  OrganizationUnitType,
  ORGANIZATION_UNITS_QUERY,
} from '@institutions/graphql';

function normalizeOrganizationUnit(
  organizationUnit: MembershipOrganizationUnit
) {
  return { ...organizationUnit, disabled: !organizationUnit.visible };
}

function formatMembership(membership: MembershipByEmail) {
  return membership
    ? {
        ...membership,
        phone: formatPhone(membership.phone),
        role: membership.role === 'worker' ? null : membership.role,
        employeeId: membership.role === 'worker' ? null : membership.employeeId,
      }
    : {};
}

function countOrganizationBusinesses(
  organizationUnits: OrganizationUnitType[]
) {
  return new Set(
    organizationUnits.map(
      (organizationUnit) => organizationUnit.department.organizationBusiness.id
    )
  ).size;
}

function moveDisabledToEnd(
  a: CustomOrganizationUnits,
  b: CustomOrganizationUnits
) {
  if (a.disabled) return 1;
  if (b.disabled) return -1;
  return 0;
}

function findOptionLabel(options: Option[], optionValue: string | undefined) {
  const label = options.find(({ value }) => value === optionValue)?.text;

  return label || optionValue || '';
}

export function MembersAddEditSteps() {
  const { email: emailParam } = useParams<{ email: string }>();

  const [existingMembership, setExistingMembership] =
    useState<MembershipByEmail>();

  const isEdition = !!existingMembership?.id;
  const routeSufix = isEdition ? `edit/${emailParam}` : 'add';

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const step = queryParams.get('step');
  const [form] = Form.useForm();

  const [createMembership, { loading: loadingCreate }] =
    useMutation<WorksMembershipCreationResponse>(WorksMembershipCreation);
  const [updateMembership, { loading: loadingUpdate }] =
    useMutation<WorksMembershipUpdateResponse>(WorksMembershipUpdate);

  const currentEntity = useCurrentEntity();
  const defaultTimeZone = currentEntity?.defaultTimeZone;

  const isOrganization = currentEntity?.entityType === 'Organization';

  const {
    data: rolesData,
    error: rolesError,
    loading: rolesLoading,
  } = useQuery<WorksMembershipRolesResponse>(WorksMembershipRolesQuery);
  const {
    error: timeZonesError,
    data: timeZonesData,
    loading: timeZonesLoading,
  } = useQuery<TimeZonesResponse>(TimeZonesQuery);
  const {
    error: organizationUnitsError,
    data: organizationUnitsData,
    loading: organizationUnitsLoading,
  } = useQuery(ORGANIZATION_UNITS_QUERY);

  const [
    organizationBusinessCountToReview,
    setOrganizationBusinessCountToReview,
  ] = useState(0);
  const [valuesToSave, setValuesToSave] = useState<Store>();
  const [emailLoaded, setEmailLoaded] = useState(false);
  const [timeZones, setTimeZones] = useState<Option[]>([]);
  const [membershipRoles, setMembershipRoles] = useState<Option[]>([]);
  const [selectedOrganizationUnitsIds, setSelectedOrganizationUnitsIds] =
    useState<string[]>([]);

  useEffect(() => {
    setBreadcrumbs([
      {
        name: 'Admin',
        path: isOrganization
          ? ORGANIZATION_ADMIN_MENU.HOME.ROUTE
          : SUPPLIER_ADMIN_MENU.HOME.ROUTE,
      },
      {
        name: 'Users',
        path: isOrganization
          ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
          : SUPPLIER_ADMIN_MENU.USERS.ROUTE,
      },
      {
        name: isEdition ? 'Update' : 'Add',
        path: `${
          isOrganization
            ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
            : SUPPLIER_ADMIN_MENU.USERS.ROUTE
        }/${routeSufix}`,
      },
    ]);
  }, [isEdition, routeSufix, isOrganization]);

  const [findMembership] = useLazyQuery<WorksMembershipByEmailResponse>(
    WorksMembershipByEmailQuery,
    {
      onCompleted(data) {
        handleMembershipLoaded(data.worksMembershipByEmail);
        setValuesToSave(data?.worksMembershipByEmail as Store);
      },
    }
  );

  useEffect(() => {
    if (step !== '1') {
      findMembership({ variables: { email: emailParam } });
    }
  }, [emailParam, findMembership, step]);

  useEffect(() => {
    if (timeZonesData) setTimeZones(timeZonesData.timeZones);
  }, [timeZonesData]);

  useEffect(() => {
    if (rolesData) setMembershipRoles(rolesData.worksMembershipRoles);
  }, [rolesData]);

  const visibleOrganizationUnits = useMemo(() => {
    return (
      /*
      There is a logic in the policy that brings the units which have relationship with the user existing assignment terms.
      As we have the visible property, we can filter them as a workaround. Issue WORK-4931.
      */
      organizationUnitsData?.organizationUnits?.filter(
        (unit) => unit.visible
      ) || []
    );
  }, [organizationUnitsData]);

  const existingOrganizationUnits = useMemo(
    () =>
      existingMembership?.organizationUnitsActive?.map(
        normalizeOrganizationUnit
      ) || [],
    [existingMembership]
  );

  const invisibleOrganizationUnits = existingOrganizationUnits.filter(
    ({ disabled }) => disabled
  );

  const organizationUnits = [
    ...visibleOrganizationUnits,
    ...invisibleOrganizationUnits,
  ];

  useEffect(() => {
    setSelectedOrganizationUnitsIds(
      [...existingOrganizationUnits].sort(moveDisabledToEnd).map(({ id }) => id)
    );
    setOrganizationBusinessCountToReview(
      existingMembership?.organizationBusinessesActiveCount || 0
    );
  }, [existingMembership, existingOrganizationUnits]);

  const initialValues = useMemo(
    () => ({
      email: emailParam,
      timeZone: defaultTimeZone,
      phone: '',
      firstName: '',
      lastName: '',
      title: '',
    }),
    [emailParam, defaultTimeZone]
  );

  const previousValues = useMemo(() => {
    if (!existingMembership) return undefined;
    return {
      ...existingMembership,
      timeZone: findOptionLabel(timeZones, existingMembership.timeZone),
      role: findOptionLabel(membershipRoles, existingMembership.role),
    };
  }, [existingMembership, membershipRoles, timeZones]);

  const loadingSave = loadingCreate || loadingUpdate;
  const loading = [
    rolesLoading,
    timeZonesLoading,
    organizationUnitsLoading,
  ].includes(true);

  const error = rolesError || timeZonesError || organizationUnitsError;

  if (error) {
    return <ErrorHandler error={error} log="MembersAddEditSteps" />;
  }

  const readOnly =
    !!existingMembership && !existingMembership.canUserUpdateMembership;

  function handleOrganizationUnitIdsChange(organizationUnitIds: string[]) {
    const selectedOrganizationUnits = organizationUnits.filter(({ id }) =>
      organizationUnitIds.includes(id)
    );

    setSelectedOrganizationUnitsIds(organizationUnitIds);
    setOrganizationBusinessCountToReview(
      countOrganizationBusinesses(selectedOrganizationUnits)
    );
  }

  function handleMembershipLoaded(membership: MembershipByEmail) {
    if (form.getFieldError('email').length) return;
    setEmailLoaded(true);
    setExistingMembership(membership);

    const formattedMembership = formatMembership(membership);
    form.setFieldsValue(formattedMembership);
  }

  function handleEmailReset() {
    setEmailLoaded(false);
    setExistingMembership(undefined);
  }

  async function validateForm() {
    try {
      await form.validateFields();
      setValuesToSave(form.getFieldsValue());
      return true;
    } catch {
      return false;
    }
  }

  function buildWorksDataMembership() {
    return {
      email: valuesToSave?.email,
      employeeId: valuesToSave?.employeeId,
      firstName: valuesToSave?.firstName,
      lastName: valuesToSave?.lastName,
      role: valuesToSave?.role,
      phone: valuesToSave?.phone,
      timeZone: valuesToSave?.timeZone,
      title: valuesToSave?.title,
      organizationUnits: selectedOrganizationUnitsIds,
    };
  }

  async function handleUpdate() {
    const worksMembershipData = buildWorksDataMembership();

    const { errors } = await updateMembership({
      variables: { id: existingMembership?.id, input: worksMembershipData },
    });

    if (errors) {
      createMessage('error', errors.map(({ message }) => message).join(' '));
      return;
    }

    createMessage('success', 'User updated');

    navigate(
      isOrganization
        ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
        : SUPPLIER_ADMIN_MENU.USERS.ROUTE,
      {
        state: {
          updatedMemberIds: [existingMembership?.id],
        },
      }
    );
  }

  async function handleCreate() {
    const worksMembershipData = buildWorksDataMembership();

    const { data, errors } = await createMembership({
      variables: {
        input: {
          ...worksMembershipData,
          email: valuesToSave?.email,
        },
      },
    });

    if (errors) {
      createMessage('error', errors.map(({ message }) => message).join(' '));
      return;
    }

    createMessage('success', 'New user added');

    const newMemberId = data?.createWorksMembership?.worksMembership?.id;
    navigate(
      isOrganization
        ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
        : SUPPLIER_ADMIN_MENU.USERS.ROUTE,
      {
        state: {
          updatedMemberIds: [newMemberId],
        },
      }
    );
  }

  function handleSave() {
    if (isEdition) {
      handleUpdate();
    } else {
      handleCreate();
    }
  }

  return (
    <>
      <PageSteps
        initialStepIndex={step ? parseInt(step) - 1 : 0}
        onCancel={() => {
          navigate(
            isOrganization
              ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
              : SUPPLIER_ADMIN_MENU.USERS.ROUTE
          );
        }}
        steps={[
          {
            title: 'User Details',
            page: (
              <LoadingWrapper loading={loading}>
                <MembersAddEditStepForm
                  form={form}
                  edit={isEdition}
                  isOrganization={isOrganization}
                  onMembershipLoaded={handleMembershipLoaded}
                  onResetEmail={handleEmailReset}
                  emailLoaded={emailLoaded}
                  initialValues={initialValues}
                  loading={loading}
                />
              </LoadingWrapper>
            ),
            validate: validateForm,
            forwardButtonDisabled: !emailLoaded || readOnly,
            headerSlot: readOnly && (
              <Banner type="info">
                You don&apos;t have the rights to edit Organizations Admin
                users.
              </Banner>
            ),
          },
          ...(isOrganization
            ? [
                {
                  title: 'Units',
                  page: (
                    <MembersAddEditStepSelectUnits
                      loading={organizationUnitsLoading}
                      organizationUnits={organizationUnits}
                      selectedOrganizationUnitIds={selectedOrganizationUnitsIds}
                      onOrganizationUnitIdsChange={
                        handleOrganizationUnitIdsChange
                      }
                    />
                  ),
                  headerSlot: !!invisibleOrganizationUnits.length && (
                    <Banner type="info">
                      You can only update locations to units that you belong to.
                    </Banner>
                  ),
                },
              ]
            : []),
          {
            title: 'Summary',
            page: (
              <MembersAddEditStepReview
                isOrganization={isOrganization}
                previousData={previousValues}
                newData={{
                  ...valuesToSave,
                  organizationUnitsCount: selectedOrganizationUnitsIds?.length,
                  organizationBusinessCount: organizationBusinessCountToReview,
                  timeZone: findOptionLabel(timeZones, valuesToSave?.timeZone),
                  role: findOptionLabel(membershipRoles, valuesToSave?.role),
                }}
              />
            ),
          },
        ]}
        onConfirm={handleSave}
        confirmLoading={loadingSave}
      />
    </>
  );
}
